import LegalPage, { Section, SubSection, LegalList } from "./LegalPage";

export default function TermsAndConditions() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Platform Terms of Service"
      version="1.0"
      effectiveDate="9 February 2026"
      testId="terms-page"
    >
      <Section number="1" title="Introduction">
        <p>
          Welcome to Vanguard Beacon. These Platform Terms of Service ("Terms") govern your access to
          and use of the Vanguard Beacon platform, including our web application, mobile applications,
          software, artificial intelligence features, reports, dashboards, documentation, and any related
          services we make available (collectively, the "Platform"). The Platform is owned and operated
          by Vanguard Beacon ("Vanguard Beacon", "we", "our", or "us"). By creating an account, accessing,
          or using the Platform, you confirm that you have read, understood, and agree to be bound by
          these Terms. If you do not agree to these Terms, you must not access or use the Platform.
        </p>
      </Section>

      <Section number="2" title="Purpose of the Platform">
        <p>
          Vanguard Beacon is a cloud-based software platform designed to assist organisations in
          digitising and managing food safety, operational, and regulatory compliance activities. The
          Platform may include features such as:
        </p>
        <LegalList>
          <li>Digital compliance records</li>
          <li>Inspection checklists</li>
          <li>Internal audit management</li>
          <li>Corrective and Preventive Action (CAPA) management</li>
          <li>Expiry date tracking</li>
          <li>Document management</li>
          <li>Task assignment and tracking</li>
          <li>AI-assisted compliance guidance</li>
          <li>Reporting and operational dashboards</li>
          <li>Workflow automation</li>
          <li>Multi-site and multi-user management</li>
          <li>Other software features introduced from time to time</li>
        </LegalList>
        <p>
          The Platform is intended to support compliance management processes and improve operational
          efficiency. It does <strong>not</strong> replace professional judgement, regulatory obligations,
          internal governance, certification requirements, or competent food safety management.
        </p>
      </Section>

      <Section number="3" title="Acceptance of these Terms">
        <p>By accessing or using the Platform, you acknowledge and agree that:</p>
        <LegalList>
          <li>you have read these Terms in full;</li>
          <li>you agree to comply with these Terms;</li>
          <li>where applicable, you have authority to bind the organisation that you represent;</li>
          <li>all users within your organisation accessing the Platform do so under your authority;</li>
          <li>your use of the Platform is subject to our Privacy Policy and any other policies referenced within these Terms.</li>
        </LegalList>
        <p>
          If you are entering into these Terms on behalf of a company, partnership, government agency,
          association or other legal entity, you represent that you are authorised to bind that entity.
          If you do not have such authority, you must not use the Platform on behalf of that entity.
        </p>
      </Section>

      <Section number="4" title="Definitions">
        <p>For the purposes of these Terms:</p>
        <LegalList>
          <li><strong>Account</strong> means a registered account created to access the Platform.</li>
          <li><strong>Administrator</strong> means a Customer-designated user with authority to manage users, permissions, settings, and subscriptions.</li>
          <li><strong>AI Features</strong> means any artificial intelligence, machine learning, generative AI, automation, recommendation engine, or similar functionality provided within the Platform.</li>
          <li><strong>Authorised User</strong> means any employee, contractor, representative, or other individual authorised by the Customer to access the Platform.</li>
          <li><strong>Customer</strong> means the organisation or individual that purchases, subscribes to, or otherwise uses the Platform.</li>
          <li><strong>Customer Data</strong> means all information, documents, records, forms, images, files, audit records, compliance records, SOPs, policies, checklists, reports, user-generated content, and other information uploaded to or created within the Platform by the Customer or its Authorised Users.</li>
          <li><strong>Personal Data</strong> has the meaning given under the Personal Data Protection Act 2012 (Singapore), as amended from time to time.</li>
          <li><strong>Platform</strong> means the Vanguard Beacon software, website, applications, APIs, AI features, dashboards, documentation, and all associated services provided by Vanguard Beacon.</li>
          <li><strong>Subscription</strong> means the paid or trial access granted to the Platform under an applicable subscription plan.</li>
          <li><strong>Terms</strong> means these Platform Terms of Service.</li>
        </LegalList>
      </Section>

      <Section number="5" title="Eligibility">
        <p>To use the Platform:</p>
        <LegalList>
          <li>you must be at least eighteen (18) years of age;</li>
          <li>you must have legal capacity to enter into binding agreements;</li>
          <li>where you are using the Platform on behalf of an organisation, you must be authorised to do so.</li>
        </LegalList>
        <p>
          The Platform is intended primarily for businesses, organisations, and commercial users. Nothing
          in these Terms creates any employment, agency, partnership, joint venture, or franchise
          relationship between Vanguard Beacon and any Customer.
        </p>
      </Section>

      <Section number="6" title="Customer Accounts">
        <p>To access certain features of the Platform, a Customer must create an Account. The Customer agrees to:</p>
        <LegalList>
          <li>provide accurate and complete registration information;</li>
          <li>keep all account information current;</li>
          <li>maintain the confidentiality of login credentials;</li>
          <li>implement appropriate access controls within its organisation;</li>
          <li>promptly notify Vanguard Beacon of any unauthorised access or suspected security incident affecting its Account.</li>
        </LegalList>
        <p>
          Each Authorised User must use their own account credentials. Customers must not share user
          accounts unless explicitly permitted by the applicable Subscription. The Customer remains
          responsible for all activities undertaken using its Accounts, including those performed by its
          employees, contractors, or other Authorised Users.
        </p>
      </Section>

      <Section number="7" title="Shared Responsibility">
        <p>
          Vanguard Beacon and the Customer acknowledge that effective compliance management is a shared
          responsibility. Vanguard Beacon is responsible for providing and maintaining the Platform with
          reasonable skill and care. The Customer is responsible for:
        </p>
        <LegalList>
          <li>maintaining accurate records;</li>
          <li>ensuring information entered into the Platform is complete and up to date;</li>
          <li>assigning appropriate personnel to compliance activities;</li>
          <li>reviewing AI-generated outputs before implementation;</li>
          <li>completing inspections, audits, corrective actions, and other operational activities;</li>
          <li>ensuring staff are appropriately trained;</li>
          <li>complying with all applicable laws, regulations, licences, standards, and certification requirements.</li>
        </LegalList>
        <p>
          The Platform assists Customers in managing compliance processes but does not perform compliance
          activities on the Customer's behalf. Responsibility for achieving and maintaining regulatory
          compliance remains solely with the Customer.
        </p>
      </Section>

      <Section number="8" title="Subscription Services">
        <SubSection number="8.1" title="Subscription Access">
          <p>
            Access to the Platform is provided on a subscription basis under the subscription plan
            selected by the Customer. Unless otherwise agreed in writing, subscriptions are
            non-exclusive, non-transferable and granted solely for the Customer's internal business
            operations. The Subscription grants the Customer a limited, revocable licence to access and
            use the Platform during the applicable subscription period, subject to these Terms.
          </p>
        </SubSection>
        <SubSection number="8.2" title="Trial Accounts">
          <p>
            Vanguard Beacon may, at its sole discretion, offer free trials, demonstration accounts or
            evaluation licences. Trial accounts are provided "as is" and may contain limitations on
            features, storage, user limits or functionality. Vanguard Beacon may suspend or terminate a
            trial account at any time without liability.
          </p>
        </SubSection>
        <SubSection number="8.3" title="Changes to Subscription Plans">
          <p>
            We may introduce new subscription plans, modify features, change usage limits or discontinue
            legacy plans from time to time. Existing Customers will be provided reasonable notice where
            such changes materially affect their Subscription.
          </p>
        </SubSection>
        <SubSection number="8.4" title="Future Features">
          <p>
            From time to time, Vanguard Beacon may introduce additional software modules, integrations,
            AI capabilities or workflow enhancements. Unless expressly stated otherwise, these Terms will
            apply to any future functionality made available through the Platform.
          </p>
        </SubSection>
      </Section>

      <Section number="9" title="Customer Responsibilities">
        <p>
          The Customer is responsible for ensuring the Platform is used appropriately within its
          organisation. Without limitation, the Customer agrees to:
        </p>
        <LegalList>
          <li>maintain accurate and truthful compliance records;</li>
          <li>ensure expiry dates and operational records are correctly entered;</li>
          <li>complete inspections and assigned tasks in a timely manner;</li>
          <li>review and verify all AI-generated content before implementation;</li>
          <li>ensure only authorised personnel access the Platform;</li>
          <li>maintain appropriate internal approval processes;</li>
          <li>promptly investigate and address issues identified through the Platform;</li>
          <li>comply with all applicable laws, regulations and licence conditions.</li>
        </LegalList>
        <p>
          The Customer acknowledges that the Platform relies upon information provided by the Customer.
          Incorrect, incomplete or outdated information may affect reports, reminders, dashboards,
          analytics and AI-generated outputs.
        </p>
        <SubSection number="9.1" title="User Training">
          <p>
            The Customer is responsible for ensuring its personnel receive appropriate training in the
            use of the Platform. Vanguard Beacon is not responsible for errors arising from improper use
            of the Platform.
          </p>
        </SubSection>
        <SubSection number="9.2" title="Internal Procedures">
          <p>
            The Customer remains responsible for maintaining appropriate internal operational procedures,
            supervisory controls and verification processes. The Platform is intended to complement—not
            replace—effective operational management.
          </p>
        </SubSection>
      </Section>

      <Section number="10" title="Acceptable Use">
        <p>The Customer must not use the Platform:</p>
        <LegalList>
          <li>for any unlawful purpose;</li>
          <li>to submit fraudulent or misleading information;</li>
          <li>to interfere with the operation of the Platform;</li>
          <li>to attempt unauthorised access to any systems;</li>
          <li>to upload malicious software or code;</li>
          <li>to reverse engineer or copy the Platform except where permitted by law;</li>
          <li>to circumvent security controls;</li>
          <li>to infringe the intellectual property rights of others;</li>
          <li>to use the Platform in any manner that may damage Vanguard Beacon's reputation or systems.</li>
        </LegalList>
        <p>The Customer must ensure that all Authorised Users comply with these Terms.</p>
        <SubSection number="10.1" title="Account Security">
          <p>Customers must implement reasonable internal security measures including:</p>
          <LegalList>
            <li>protecting passwords;</li>
            <li>restricting administrator access;</li>
            <li>removing access for departed employees;</li>
            <li>using strong authentication where available.</li>
          </LegalList>
          <p>
            The Customer remains responsible for activities conducted through its Accounts unless caused
            by Vanguard Beacon's negligence.
          </p>
        </SubSection>
      </Section>

      <Section number="11" title="Artificial Intelligence Features">
        <p>
          The Platform may include AI-assisted functionality designed to support operational efficiency.
          Such features may include:
        </p>
        <LegalList>
          <li>document drafting;</li>
          <li>compliance recommendations;</li>
          <li>report summaries;</li>
          <li>workflow suggestions;</li>
          <li>risk identification;</li>
          <li>action recommendations;</li>
          <li>natural language search;</li>
          <li>other AI-assisted functionality introduced from time to time.</li>
        </LegalList>
        <p>AI Features are intended to assist human decision-making. They do not replace professional judgement.</p>
        <SubSection number="11.1" title="Customer Review Required">
          <p>
            The Customer must independently review all AI-generated outputs before relying upon or
            implementing them. AI-generated content should be considered a draft or recommendation unless
            independently verified by appropriately authorised personnel.
          </p>
        </SubSection>
        <SubSection number="11.2" title="No Professional Advice">
          <p>AI-generated outputs do not constitute:</p>
          <LegalList>
            <li>legal advice;</li>
            <li>regulatory advice;</li>
            <li>food safety advice;</li>
            <li>certification advice;</li>
            <li>quality assurance consultancy;</li>
            <li>engineering advice;</li>
            <li>professional services.</li>
          </LegalList>
          <p>
            Customers remain responsible for determining whether AI-generated outputs are appropriate for
            their own operations.
          </p>
        </SubSection>
        <SubSection number="11.3" title="AI Limitations">
          <p>
            The Customer acknowledges that artificial intelligence may produce inaccurate, incomplete,
            outdated or inappropriate outputs. Accordingly, Vanguard Beacon does not warrant that
            AI-generated content will be accurate, complete or suitable for any particular purpose.
          </p>
        </SubSection>
      </Section>

      <Section number="12" title="Food Compliance Disclaimer">
        <p>
          The Platform is designed to assist organisations in managing food safety and operational
          compliance activities. However, Vanguard Beacon does not:
        </p>
        <LegalList>
          <li>certify compliance;</li>
          <li>conduct food safety audits;</li>
          <li>approve food products;</li>
          <li>provide regulatory approvals;</li>
          <li>issue certifications;</li>
          <li>perform inspections;</li>
          <li>replace Food Safety Officers or quality personnel.</li>
        </LegalList>
        <p>
          The Platform is a software tool that supports compliance management. Responsibility for
          compliance remains with the Customer.
        </p>
        <SubSection number="12.1" title="No Guarantee of Compliance">
          <p>Use of the Platform does not guarantee:</p>
          <LegalList>
            <li>compliance with applicable laws;</li>
            <li>compliance with licence conditions;</li>
            <li>compliance with customer requirements;</li>
            <li>successful audit outcomes;</li>
            <li>successful certification;</li>
            <li>successful regulatory inspections;</li>
            <li>prevention of food safety incidents;</li>
            <li>prevention of product recalls;</li>
            <li>avoidance of regulatory enforcement action.</li>
          </LegalList>
          <p>
            Compliance depends upon numerous factors beyond the Platform, including the Customer's
            operational practices, employee conduct, data accuracy and management oversight.
          </p>
        </SubSection>
        <SubSection number="12.2" title="Regulatory Responsibility">
          <p>
            The Customer remains solely responsible for complying with all applicable legislation,
            regulations and industry standards relevant to its business. These may include, where
            applicable:
          </p>
          <LegalList>
            <li>Singapore Food Agency (SFA) requirements;</li>
            <li>HACCP principles;</li>
            <li>ISO 22000;</li>
            <li>Good Manufacturing Practices (GMP);</li>
            <li>customer-specific food safety programmes;</li>
            <li>any other applicable legal or contractual obligations.</li>
          </LegalList>
          <p>
            Nothing within the Platform should be interpreted as confirming that the Customer satisfies
            any regulatory requirement.
          </p>
        </SubSection>
      </Section>

      <Section number="13" title="Expiry Tracking">
        <p>
          Where the Platform provides expiry date tracking or inventory monitoring functionality, such
          functionality is dependent upon accurate information entered by the Customer. The Customer
          remains solely responsible for:
        </p>
        <LegalList>
          <li>entering correct product information;</li>
          <li>entering correct manufacture and expiry dates;</li>
          <li>rotating stock appropriately;</li>
          <li>disposing of expired products;</li>
          <li>investigating overdue inventory;</li>
          <li>taking appropriate corrective actions.</li>
        </LegalList>
        <p>
          Vanguard Beacon shall not be liable for losses arising from inaccurate, incomplete or delayed
          information entered by the Customer.
        </p>
        <SubSection number="13.1" title="Notifications and Reminders">
          <p>
            The Platform may generate reminders, alerts or notifications. Such notifications are intended
            as operational aids only. The Customer remains responsible for ensuring appropriate action is
            taken regardless of whether a notification is received, viewed or acted upon. Vanguard Beacon
            shall not be liable for missed notifications resulting from internet outages, email delivery
            failures, user settings, third-party service interruptions or other circumstances beyond its
            reasonable control.
          </p>
        </SubSection>
      </Section>

      <Section number="14" title="Customer Data">
        <SubSection number="14.1" title="Ownership of Customer Data">
          <p>
            Vanguard Beacon recognises that the information stored within the Platform represents an
            important part of the Customer's business operations. As between Vanguard Beacon and the
            Customer, the Customer retains all right, title and interest in and to all Customer Data
            uploaded to, created within, or otherwise processed through the Platform. Nothing in these
            Terms transfers ownership of Customer Data to Vanguard Beacon.
          </p>
        </SubSection>
        <SubSection number="14.2" title="Licence to Process Customer Data">
          <p>
            To provide the Platform and associated services, the Customer grants Vanguard Beacon a
            limited, worldwide, non-exclusive licence to host, store, process, transmit, copy and display
            Customer Data solely for the purpose of:
          </p>
          <LegalList>
            <li>operating the Platform;</li>
            <li>providing requested services;</li>
            <li>maintaining system security;</li>
            <li>generating reports and dashboards;</li>
            <li>providing AI-assisted functionality;</li>
            <li>responding to support requests;</li>
            <li>complying with applicable legal obligations.</li>
          </LegalList>
          <p>This licence continues only for as long as necessary to provide the Services or as otherwise required by law.</p>
        </SubSection>
        <SubSection number="14.3" title="Accuracy of Customer Data">
          <p>
            The Customer is solely responsible for ensuring that Customer Data is accurate, complete and
            up to date. Vanguard Beacon does not verify, audit or validate Customer Data entered into the
            Platform. Reports, reminders, analytics and AI-generated outputs depend on the quality and
            completeness of Customer Data.
          </p>
        </SubSection>
        <SubSection number="14.4" title="Customer Responsibility for Records">
          <p>The Customer remains responsible for:</p>
          <LegalList>
            <li>maintaining legally required records;</li>
            <li>ensuring records are truthful;</li>
            <li>retaining records for applicable statutory periods;</li>
            <li>complying with regulatory recordkeeping requirements.</li>
          </LegalList>
          <p>Use of the Platform does not relieve the Customer of any legal obligation relating to record retention.</p>
        </SubSection>
      </Section>

      <Section number="15" title="Privacy and Personal Data">
        <p>
          Vanguard Beacon is committed to protecting Personal Data and handling such information
          responsibly. Our collection, use and disclosure of Personal Data is governed by our Privacy
          Policy, which forms part of these Terms. Where applicable, Vanguard Beacon will comply with
          the Personal Data Protection Act 2012 (Singapore) ("PDPA") and any other applicable data
          protection laws.
        </p>
        <SubSection number="15.1" title="Customer Responsibilities">
          <p>
            Where the Customer uploads Personal Data relating to employees, contractors, suppliers or
            other individuals, the Customer represents and warrants that it has obtained all necessary
            rights, permissions and consents required by applicable law. The Customer remains
            responsible for ensuring that its collection and use of Personal Data complies with
            applicable legislation.
          </p>
        </SubSection>
        <SubSection number="15.2" title="Data Security">
          <p>
            Vanguard Beacon implements commercially reasonable administrative, technical and
            organisational safeguards designed to protect Customer Data from unauthorised access,
            disclosure, alteration or destruction. However, no online service can guarantee absolute
            security. Accordingly, Vanguard Beacon does not warrant that the Platform will be completely
            free from security risks.
          </p>
        </SubSection>
      </Section>

      <Section number="16" title="Intellectual Property">
        <SubSection number="16.1" title="Ownership of the Platform">
          <p>Vanguard Beacon and its licensors retain all intellectual property rights in and to the Platform, including:</p>
          <LegalList>
            <li>software;</li>
            <li>source code;</li>
            <li>object code;</li>
            <li>AI models;</li>
            <li>workflows;</li>
            <li>databases;</li>
            <li>user interfaces;</li>
            <li>reports;</li>
            <li>documentation;</li>
            <li>branding;</li>
            <li>logos;</li>
            <li>designs;</li>
            <li>graphics;</li>
            <li>proprietary methodologies;</li>
            <li>updates and enhancements.</li>
          </LegalList>
          <p>Except for the limited licence granted under these Terms, no intellectual property rights are transferred to the Customer.</p>
        </SubSection>
        <SubSection number="16.2" title="Customer Content">
          <p>The Customer retains ownership of:</p>
          <LegalList>
            <li>uploaded documents;</li>
            <li>SOPs;</li>
            <li>policies;</li>
            <li>forms;</li>
            <li>audit reports;</li>
            <li>compliance records;</li>
            <li>checklists;</li>
            <li>photographs;</li>
            <li>operational documentation.</li>
          </LegalList>
          <p>Vanguard Beacon claims no ownership over Customer Content.</p>
        </SubSection>
        <SubSection number="16.3" title="Feedback">
          <p>
            The Customer may voluntarily provide suggestions, ideas or feedback regarding the Platform.
            Unless otherwise agreed in writing, Vanguard Beacon may use such feedback to improve the
            Platform without restriction or obligation to compensate the Customer.
          </p>
        </SubSection>
      </Section>

      <Section number="17" title="Confidentiality">
        <p>Both parties acknowledge that they may receive confidential information belonging to the other. Each party agrees to:</p>
        <LegalList>
          <li>keep confidential information secure;</li>
          <li>use confidential information only for purposes related to these Terms;</li>
          <li>not disclose confidential information except where authorised or required by law.</li>
        </LegalList>
        <p>Confidential information does not include information that:</p>
        <LegalList>
          <li>is publicly available;</li>
          <li>was already lawfully known;</li>
          <li>is independently developed without reference to the confidential information;</li>
          <li>is lawfully obtained from another source.</li>
        </LegalList>
        <p>The obligations under this section continue after termination of these Terms.</p>
      </Section>

      <Section number="18" title="Service Availability">
        <p>
          Vanguard Beacon will use commercially reasonable efforts to make the Platform available on an
          ongoing basis. However, uninterrupted availability cannot be guaranteed. The Platform may
          occasionally be unavailable due to:
        </p>
        <LegalList>
          <li>scheduled maintenance;</li>
          <li>emergency maintenance;</li>
          <li>software updates;</li>
          <li>infrastructure failures;</li>
          <li>internet outages;</li>
          <li>cybersecurity incidents;</li>
          <li>events beyond our reasonable control.</li>
        </LegalList>
        <p>Where reasonably practicable, Vanguard Beacon will endeavour to provide advance notice of planned maintenance.</p>
        <SubSection number="18.1" title="Updates">
          <p>
            Vanguard Beacon may release updates, bug fixes, security patches and feature improvements
            from time to time. Such updates may occur automatically or require temporary interruption of
            the Platform.
          </p>
        </SubSection>
        <SubSection number="18.2" title="Beta Features">
          <p>
            From time to time, Vanguard Beacon may provide experimental or beta features. Beta features
            are provided for evaluation purposes and may be modified, suspended or discontinued without
            notice. Unless otherwise stated, beta features are provided "as is" without warranty.
          </p>
        </SubSection>
      </Section>

      <Section number="19" title="Fees and Payment">
        <p>
          Where the Platform is provided under a paid Subscription, the Customer agrees to pay all
          applicable fees in accordance with the selected subscription plan. Subscription fees are
          payable in advance unless otherwise agreed.
        </p>
        <SubSection number="19.1" title="Taxes">
          <p>
            Unless otherwise stated, all fees are exclusive of applicable taxes, duties or government
            charges, including Goods and Services Tax (GST), which shall be payable by the Customer
            where required.
          </p>
        </SubSection>
        <SubSection number="19.2" title="Late Payment">
          <p>
            Where payment remains outstanding beyond the applicable due date, Vanguard Beacon may
            suspend access to the Platform until payment has been received. Reasonable notice will
            ordinarily be provided before any suspension for non-payment.
          </p>
        </SubSection>
        <SubSection number="19.3" title="Fee Changes">
          <p>
            Vanguard Beacon may revise subscription pricing from time to time. Any price changes will
            apply only after reasonable advance notice and will not affect any prepaid subscription
            period already paid for by the Customer.
          </p>
        </SubSection>
      </Section>

      <Section number="20" title="Suspension and Termination">
        <SubSection number="20.1" title="Suspension">
          <p>Vanguard Beacon may suspend or restrict access to the Platform where reasonably necessary to:</p>
          <LegalList>
            <li>protect the security or integrity of the Platform;</li>
            <li>investigate suspected unlawful activity;</li>
            <li>prevent misuse of the Platform;</li>
            <li>address material breaches of these Terms;</li>
            <li>comply with legal or regulatory requirements;</li>
            <li>respond to non-payment of subscription fees.</li>
          </LegalList>
          <p>
            Where reasonably practicable, Vanguard Beacon will provide prior notice and an opportunity
            for the Customer to remedy the issue before suspending access.
          </p>
        </SubSection>
        <SubSection number="20.2" title="Termination by Customer">
          <p>
            The Customer may terminate its Subscription in accordance with its applicable subscription
            plan. Termination does not relieve the Customer of any obligation to pay fees accrued prior
            to the effective date of termination.
          </p>
        </SubSection>
        <SubSection number="20.3" title="Termination by Vanguard Beacon">
          <p>Vanguard Beacon may terminate these Terms immediately where the Customer:</p>
          <LegalList>
            <li>commits a material breach of these Terms and fails to remedy the breach within a reasonable period after notice;</li>
            <li>repeatedly breaches these Terms;</li>
            <li>engages in fraudulent, illegal or abusive conduct;</li>
            <li>intentionally compromises the security or operation of the Platform;</li>
            <li>becomes insolvent or enters liquidation, judicial management or equivalent proceedings.</li>
          </LegalList>
        </SubSection>
        <SubSection number="20.4" title="Effect of Termination">
          <p>Upon termination:</p>
          <LegalList>
            <li>the Customer's licence to use the Platform immediately ceases;</li>
            <li>access to the Platform may be disabled;</li>
            <li>Customer Data will be retained and deleted in accordance with our Privacy Policy and data retention practices;</li>
            <li>provisions intended to survive termination shall remain in effect.</li>
          </LegalList>
        </SubSection>
      </Section>

      <Section number="21" title="Disclaimer of Warranties">
        <p>
          The Platform is provided on an "as available" and "as updated" basis. Vanguard Beacon will use
          commercially reasonable efforts to maintain the availability, functionality and security of
          the Platform. However, except as expressly stated in these Terms and to the maximum extent
          permitted by applicable law, Vanguard Beacon does not warrant that:
        </p>
        <LegalList>
          <li>the Platform will be uninterrupted or error-free;</li>
          <li>every defect will be corrected immediately;</li>
          <li>the Platform will meet every operational requirement of the Customer;</li>
          <li>AI-generated outputs will always be accurate, complete or suitable for a particular purpose;</li>
          <li>use of the Platform will guarantee regulatory compliance, audit success or certification.</li>
        </LegalList>
        <p>Nothing in these Terms excludes any rights that cannot lawfully be excluded under applicable law.</p>
      </Section>

      <Section number="22" title="Limitation of Liability">
        <SubSection number="22.1" title="General Principle">
          <p>
            Vanguard Beacon's liability is intended to be proportionate to the subscription services
            provided. To the maximum extent permitted by law, Vanguard Beacon shall not be liable for
            indirect, incidental, consequential, exemplary or special damages arising out of or relating
            to the use of the Platform.
          </p>
        </SubSection>
        <SubSection number="22.2" title="Excluded Losses">
          <p>Without limitation, Vanguard Beacon shall not be liable for:</p>
          <LegalList>
            <li>loss of profits;</li>
            <li>loss of revenue;</li>
            <li>loss of business opportunities;</li>
            <li>business interruption;</li>
            <li>reputational damage;</li>
            <li>regulatory penalties or fines;</li>
            <li>food recalls;</li>
            <li>product wastage;</li>
            <li>spoilage of inventory;</li>
            <li>customer claims;</li>
            <li>loss arising from inaccurate Customer Data;</li>
            <li>failure by the Customer to complete required compliance activities;</li>
            <li>failure to act on reminders, alerts or notifications;</li>
            <li>decisions made using AI-generated outputs without appropriate review.</li>
          </LegalList>
        </SubSection>
        <SubSection number="22.3" title="Liability Cap">
          <p>
            Subject to applicable law, Vanguard Beacon's total aggregate liability arising out of or
            relating to these Terms shall not exceed the total subscription fees paid by the Customer to
            Vanguard Beacon during the twelve (12) months immediately preceding the event giving rise to
            the claim.
          </p>
        </SubSection>
        <SubSection number="22.4" title="Exceptions">
          <p>Nothing in these Terms limits liability for:</p>
          <LegalList>
            <li>fraud or fraudulent misrepresentation;</li>
            <li>wilful misconduct;</li>
            <li>death or personal injury caused by negligence where liability cannot lawfully be excluded;</li>
            <li>any liability that cannot be excluded under applicable law.</li>
          </LegalList>
        </SubSection>
      </Section>

      <Section number="23" title="Customer Indemnity">
        <p>
          The Customer agrees to indemnify and hold harmless Vanguard Beacon, its directors, officers,
          employees and affiliates against claims, losses, liabilities, damages, costs and expenses
          arising from:
        </p>
        <LegalList>
          <li>the Customer's breach of these Terms;</li>
          <li>misuse of the Platform;</li>
          <li>inaccurate or misleading Customer Data;</li>
          <li>infringement of third-party rights by Customer Data;</li>
          <li>violation of applicable laws or regulations by the Customer;</li>
          <li>reliance on AI-generated outputs without appropriate human review.</li>
        </LegalList>
        <p>This indemnity does not apply to the extent that any claim results directly from Vanguard Beacon's own negligence or wilful misconduct.</p>
      </Section>

      <Section number="24" title="Force Majeure">
        <p>
          Neither party shall be liable for delays or failures to perform obligations caused by events
          beyond its reasonable control, including but not limited to: natural disasters; acts of
          government; war; terrorism; labour disputes; widespread internet outages; failures of cloud
          infrastructure providers; cyber attacks; pandemics; power failures. The affected party shall
          use reasonable efforts to resume performance as soon as reasonably practicable.
        </p>
      </Section>

      <Section number="25" title="Changes to the Platform and these Terms">
        <p>
          Vanguard Beacon may enhance, modify or discontinue features of the Platform from time to time
          to improve functionality, security or user experience. Where changes to these Terms materially
          affect Customer rights or obligations, Vanguard Beacon will provide reasonable notice through
          the Platform, email or other appropriate means. Continued use of the Platform after the
          effective date of updated Terms constitutes acceptance of the revised Terms.
        </p>
      </Section>

      <Section number="26" title="Governing Law and Dispute Resolution">
        <p>
          These Terms are governed by and shall be construed in accordance with the laws of the Republic
          of Singapore. The parties shall first endeavour to resolve any dispute through good faith
          discussions. If a dispute cannot be resolved amicably within thirty (30) days, either party
          may refer the dispute to the courts of Singapore, which shall have exclusive jurisdiction,
          unless otherwise agreed in writing.
        </p>
      </Section>

      <Section number="27" title="General Provisions">
        <LegalList>
          <li><strong>Entire Agreement.</strong> These Terms, together with the Privacy Policy and any applicable Subscription Agreement, constitute the entire agreement between the parties regarding the Platform.</li>
          <li><strong>Severability.</strong> If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.</li>
          <li><strong>No Waiver.</strong> Failure by either party to enforce any provision of these Terms shall not constitute a waiver of that provision or any other rights.</li>
          <li><strong>Assignment.</strong> The Customer may not assign or transfer its rights or obligations under these Terms without Vanguard Beacon's prior written consent. Vanguard Beacon may assign these Terms as part of a merger, acquisition, corporate restructuring or sale of substantially all of its assets.</li>
          <li><strong>Relationship of the Parties.</strong> Nothing in these Terms creates a partnership, agency, employment or joint venture relationship between Vanguard Beacon and the Customer.</li>
          <li><strong>Survival.</strong> The provisions relating to intellectual property, confidentiality, limitation of liability, indemnity, dispute resolution and any other provisions intended by their nature to survive shall remain effective after termination.</li>
        </LegalList>
      </Section>

      <Section number="28" title="Contact Information">
        <p>Questions regarding these Terms may be directed to:</p>
        <p>
          <strong>Vanguard Beacon</strong>
          <br />
          Email: <a href="mailto:hello@vanguardbeacon.com" className="text-brand-orange hover:underline">hello@vanguardbeacon.com</a>
          <br />
          Website: <a href="https://vanguardbeacon.com" className="text-brand-orange hover:underline">https://vanguardbeacon.com</a>
        </p>
      </Section>
    </LegalPage>
  );
}
